import { useState } from 'react'
import { ExternalLink, AlertCircle } from 'lucide-react'
import { api } from '../../api/client'

export interface CalendarProposal {
  id: string
  status: 'pending' | 'confirmed' | 'rejected' | 'failed'
  summary: string
  start_iso: string
  end_iso: string
  attendees: string[]
  description?: string | null
  location?: string | null
  add_meet_link: boolean
  google_event_id?: string | null
  google_html_link?: string | null
  error_message?: string | null
  created_at: string
  resolved_at?: string | null
}

interface Props {
  proposal: CalendarProposal
  onChange?: (updated: CalendarProposal) => void
}

const MONTHS_PT = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ']

function formatSubtitle(startIso: string, endIso: string, description?: string | null): string {
  try {
    const start = new Date(startIso)
    const end = new Date(endIso)
    const timeFmt: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
    const timeRange = `${start.toLocaleTimeString('pt-PT', timeFmt)}–${end.toLocaleTimeString('pt-PT', timeFmt)}`
    // Use description if short, else just show time range
    if (description && description.length < 80) return description
    return `Proposto pelo seGuru — ${timeRange}`
  } catch {
    return description || ''
  }
}

export function CalendarProposalCard({ proposal, onChange }: Props) {
  const [busy, setBusy] = useState<'confirm' | 'reject' | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  const start = new Date(proposal.start_iso)
  const month = MONTHS_PT[start.getMonth()]
  const day = start.getDate()
  const subtitle = formatSubtitle(proposal.start_iso, proposal.end_iso, proposal.description)

  const hasAttendees = proposal.attendees.length > 0
  const confirmLabel = hasAttendees ? 'Confirmar e enviar' : 'Confirmar'

  const act = async (action: 'confirm' | 'reject') => {
    setBusy(action)
    setLocalError(null)
    try {
      const updated = await api.post<CalendarProposal>(
        `/chat/calendar-proposals/${proposal.id}/${action}`,
      )
      onChange?.(updated)
    } catch (e: any) {
      setLocalError(e.message || 'Pedido falhou')
    } finally {
      setBusy(null)
    }
  }

  const isRejected = proposal.status === 'rejected'

  return (
    <div
      style={{
        border: '1px solid rgba(26, 24, 20, 0.18)',
        borderRadius: '3px',
        boxShadow: '1px 1px 0 rgba(26, 24, 20, 0.1)',
        padding: '12px 14px',
        background: isRejected ? 'rgba(0,0,0,0.02)' : 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        opacity: isRejected ? 0.55 : 1,
        fontFamily: 'var(--sans)',
        maxWidth: '560px',
      }}
    >
      {/* Date box */}
      <div
        style={{
          width: '38px',
          height: '38px',
          borderRadius: '6px',
          background: 'var(--cream)',
          border: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '9px', color: 'var(--accent-warm)', letterSpacing: '0.06em', fontWeight: 600, textTransform: 'uppercase', lineHeight: 1 }}>{month}</span>
        <span style={{ fontFamily: 'var(--serif)', fontSize: '17px', color: 'var(--ink)', lineHeight: 1, marginTop: '1px' }}>{day}</span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {proposal.summary}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--ink-muted)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {subtitle}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
        {proposal.status === 'pending' && (
          <>
            <button
              onClick={() => act('reject')}
              disabled={busy !== null}
              style={{
                padding: '6px 11px',
                borderRadius: '3px',
                border: '1px solid rgba(26, 24, 20, 0.18)',
                background: 'transparent',
                color: 'var(--ink-muted)',
                fontFamily: 'var(--sans)',
                fontSize: '12.5px',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.5 : 1,
              }}
            >
              Rejeitar
            </button>
            <button
              onClick={() => act('confirm')}
              disabled={busy !== null}
              style={{
                padding: '6px 11px',
                borderRadius: '3px',
                border: '1px solid var(--ink)',
                background: 'var(--ink)',
                color: 'white',
                fontFamily: 'var(--sans)',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.5 : 1,
              }}
            >
              {busy === 'confirm' ? 'A criar...' : confirmLabel}
            </button>
          </>
        )}

        {proposal.status === 'confirmed' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent)', padding: '2px 8px', border: '1px solid var(--accent)', borderRadius: '2px', letterSpacing: '0.04em' }}>
              Criado
            </span>
            {proposal.google_html_link && (
              <a
                href={proposal.google_html_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--ink-muted)', textDecoration: 'none' }}
              >
                Ver no Calendar <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {proposal.status === 'rejected' && (
          <span style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>Rejeitado</span>
        )}

        {proposal.status === 'failed' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#991b1b' }}>
              <AlertCircle size={12} /> Erro
            </span>
            <button
              onClick={() => act('confirm')}
              disabled={busy !== null}
              style={{ fontSize: '11px', color: '#dc2626', background: 'none', border: '1px solid #dc2626', borderRadius: '2px', padding: '2px 8px', cursor: 'pointer' }}
            >
              Tentar de novo
            </button>
          </div>
        )}
      </div>

      {localError && (
        <div style={{ position: 'absolute', bottom: '-28px', left: 0, fontSize: '11px', color: '#991b1b' }}>
          {localError}
        </div>
      )}
    </div>
  )
}
